import {
  ClipboardList,
  BellRing,
  MessageSquareText,
  CalendarPlus,
  ChevronDown,
  FolderKanban,
  MapPinned,
  ChartNoAxesCombined,
  PackageSearch,
  LayoutDashboard,
  ListFilter,
  LogOut,
  PanelTopOpen,
  UsersRound,
  FlaskConical,
  History,
} from "lucide-react";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Sparkles } from "lucide-react";

const OPERATIONS_API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;
const notificationKeyByRoute = {
  "/action-queue": "action_queue",
  "/communications": "communications",
};

const navigation = [
  {
    label: "Dashboard",
    to: "/dashboard",
    icon: LayoutDashboard,
    testId: "board-nav-operations-desk",
  },
  {
    label: "AI Assistant",
    to: "/ai-assistant",
    icon: Sparkles,
    testId: "board-nav-ai-assistant",
    requires: { permission: "ai_assistant:use", feature: "ai_assistant" },
  },
  {
    label: "Master Board",
    to: "/master-project-board",
    icon: PanelTopOpen,
    testId: "board-nav-master",
  },
  {
    label: "Manager Board",
    to: "/manager-board",
    icon: ClipboardList,
    testId: "board-nav-manager",
  },
];

const billingNavigation = {
  label: "Billing",
  to: "/billing",
  icon: FolderKanban,
  testId: "board-nav-billing",
};

const userNavigation = {
  label: "Users",
  to: "/users",
  icon: UsersRound,
  testId: "board-nav-users",
  requires: { permission: "users:manage" },
};

const actionQueueNavigation = {
  label: "Action Queue",
  to: "/action-queue",
  icon: BellRing,
  testId: "board-nav-action-queue",
};
const activityNavigation = {
  label: "Activity History",
  to: "/project-activity",
  icon: History,
  testId: "board-nav-project-activity",
};
const communicationNavigation = {
  label: "Communication Log",
  to: "/communications",
  icon: MessageSquareText,
  testId: "board-nav-communications",
};
const appointmentsNavigation = {
  label: "Appointments",
  to: "/appointments",
  icon: CalendarPlus,
  testId: "board-nav-appointments",
};
const permitsNavigation = {
  label: "Permits & Locates",
  to: "/permits-locates",
  icon: MapPinned,
  testId: "board-nav-permits-locates",
};
const analyticsNavigation = {
  label: "Analytics",
  to: "/analytics",
  icon: ChartNoAxesCombined,
  testId: "board-nav-analytics",
  requires: { permission: "analytics:read", feature: "analytics" },
};
const assetsNavigation = {
  label: "Inventory & Assets",
  to: "/assets",
  icon: PackageSearch,
  testId: "board-nav-assets",
  requires: { permission: "assets:manage" },
};
const ownerLabNavigation = {
  label: "Owner Lab",
  to: "/owner-lab",
  icon: FlaskConical,
  testId: "board-nav-owner-lab",
};

const roleLabel = (role) =>
  ({
    office_admin: "Office/Admin",
    owner_admin: "Company Owner/Admin",
    project_manager: "Project Manager",
    supervisor: "Supervisor",
  })[role] || role?.replaceAll("_", " ");

export default function BoardWorkspace({
  children,
  eyebrow,
  title,
  subtitle,
  actions,
  headingTestId,
}) {
  const { canAccessBilling, hasPermission, isFeatureEnabled, logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const touchStart = useRef(null);
  const [openFolder, setOpenFolder] = useState(null);
  const [folderPosition, setFolderPosition] = useState(null);
  const [notificationBadges, setNotificationBadges] = useState({});
  const loadNotificationBadges = useCallback(async () => {
    try {
      const response = await axios.get(`${OPERATIONS_API}/navigation-badges`);
      setNotificationBadges(response.data || {});
    } catch {
      setNotificationBadges({});
    }
  }, []);
  const navItems = [
    ...navigation.filter((item) => {
      if (item.to === "/manager-board" && user?.role === "supervisor") return false;
      if (item.requires) {
        if (item.requires.permission && !hasPermission(item.requires.permission)) return false;
        if (item.requires.feature && !isFeatureEnabled(item.requires.feature)) return false;
      }
      return true;
    }),
    ...(user?.role === "supervisor" ? [] : [actionQueueNavigation]),
    communicationNavigation,
    ...(user?.role === "supervisor" ? [] : [appointmentsNavigation]),
    ...(user?.role === "supervisor" ? [] : [permitsNavigation]),
    ...(user?.role === "owner_admin" ? [ownerLabNavigation] : []),
  ];
  const passesRequires = (item) =>
    (!item.requires?.permission || hasPermission(item.requires.permission)) &&
    (!item.requires?.feature || isFeatureEnabled(item.requires.feature));
  const userFolderItems = [
    ...(user?.role === "supervisor" ? [] : [activityNavigation]),
    userNavigation,
    assetsNavigation,
  ].filter(passesRequires);
  const billingFolderItems = [
    ...(canAccessBilling ? [billingNavigation] : []),
    analyticsNavigation,
  ].filter(passesRequires);
  const swipeItems = [
    ...navigation,
    ...(user?.role === "supervisor" ? [] : [actionQueueNavigation]),
    communicationNavigation,
    ...(user?.role === "supervisor" ? [] : [appointmentsNavigation]),
    ...(user?.role === "supervisor" ? [] : [permitsNavigation]),
    ...(canAccessBilling ? [billingNavigation] : []),
    analyticsNavigation,
    userNavigation,
    assetsNavigation,
  ].filter((item) => (item.to !== "/manager-board" || user?.role !== "supervisor") && passesRequires(item));
  const handleTouchStart = (event) => {
    if (
      event.target.closest(
        "input, textarea, select, button, .board-table-scroll, .operations-board-nav",
      )
    ) {
      return;
    }
    touchStart.current = { x: event.touches[0].clientX, y: event.touches[0].clientY };
  };
  const handleTouchEnd = (event) => {
    if (!touchStart.current) return;
    const deltaX = event.changedTouches[0].clientX - touchStart.current.x;
    const deltaY = event.changedTouches[0].clientY - touchStart.current.y;
    touchStart.current = null;
    if (Math.abs(deltaX) < 80 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.5) return;
    const index = swipeItems.findIndex((item) => item.to === location.pathname);
    if (index < 0) return;
    const nextIndex = deltaX < 0 ? index + 1 : index - 1;
    if (swipeItems[nextIndex]) navigate(swipeItems[nextIndex].to);
  };
  const signOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };
  const toggleFolder = (folder, event) => {
    if (openFolder === folder) {
      setOpenFolder(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    setFolderPosition({
      left: Math.max(8, Math.min(rect.left, window.innerWidth - 158)),
      top: rect.bottom + 4,
    });
    setOpenFolder(folder);
  };
  useEffect(() => {
    const closeFolder = (event) => {
      if (!event.target.closest(".operations-nav-folder-toggle, .operations-nav-menu-portal")) {
        setOpenFolder(null);
      }
    };
    window.addEventListener("pointerdown", closeFolder);
    return () => window.removeEventListener("pointerdown", closeFolder);
  }, []);
  useEffect(() => {
    loadNotificationBadges();
    window.addEventListener("jfm:badge-refresh", loadNotificationBadges);
    return () => {
      window.removeEventListener("jfm:badge-refresh", loadNotificationBadges);
    };
  }, [loadNotificationBadges, location.pathname]);
  return (
    <div
      className="operations-board-shell"
      data-testid="operations-board-shell"
      onTouchEnd={handleTouchEnd}
      onTouchStart={handleTouchStart}
    >
      <header className="operations-board-topbar">
        <Link
          className="operations-brand"
          data-testid="board-brand-link"
          to="/"
        >
          <span className="operations-brand-mark" aria-hidden="true">
            J
          </span>
          <span>
            <strong>JFM</strong>
            <small>OPERATIONS</small>
          </span>
        </Link>
        <nav className="operations-board-nav" aria-label="Operations boards">
          {navItems.map(({ label, to, icon: Icon, testId }) => {
            const notificationKey = notificationKeyByRoute[to];
            const badgeCount = notificationKey ? notificationBadges[notificationKey] || 0 : 0;
            return (
              <NavLink
                aria-label={badgeCount ? `${label}, ${badgeCount} open items` : label}
                className={({ isActive }) =>
                  `operations-nav-link${isActive ? " is-active" : ""}`
                }
                data-testid={testId}
                key={to}
                to={to}
              >
                <Icon size={15} />
                <span>{label}</span>
                {badgeCount > 0 && (
                  <span
                    className="operations-nav-notification-badge"
                    data-testid={`${testId}-notification-badge`}
                  >
                    {badgeCount > 99 ? "99+" : badgeCount}
                  </span>
                )}
              </NavLink>
            );
          })}
          {billingFolderItems.length > 0 && (
            <button
              aria-expanded={openFolder === "billing"}
              className="operations-nav-folder-toggle"
              data-testid="board-nav-billing-folder"
              onClick={(event) => toggleFolder("billing", event)}
              type="button"
            >
                <span>Billing</span>
                <ChevronDown aria-hidden="true" size={14} />
            </button>
          )}
          {userFolderItems.length > 0 && (
            <button
              aria-expanded={openFolder === "users"}
              className="operations-nav-folder-toggle"
              data-testid="board-nav-users-folder"
              onClick={(event) => toggleFolder("users", event)}
              type="button"
            >
                <span>Users</span>
                <ChevronDown aria-hidden="true" size={14} />
            </button>
          )}
        </nav>
        <div
          className="operations-user-menu"
          data-testid="operations-user-menu"
        >
          <span data-testid="operations-user-role">
            {user?.name} · {roleLabel(user?.role)}
          </span>
          <button
            data-testid="operations-logout-button"
            onClick={signOut}
            type="button"
          >
            <LogOut size={14} />
            Sign out
          </button>
        </div>
      </header>
      {openFolder && folderPosition && (
        <div
          className="operations-nav-menu-portal"
          data-testid={`board-nav-${openFolder}-menu`}
          style={folderPosition}
        >
          {(openFolder === "billing" ? billingFolderItems : userFolderItems).map((item) => (
            <NavLink
              data-testid={item.testId}
              key={item.to}
              onClick={() => setOpenFolder(null)}
              to={item.to}
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      )}
      <main className="operations-board-main">
        <section
          className="operations-board-heading"
          data-testid="board-page-heading"
        >
          <div>
            <p className="operations-kicker">{eyebrow}</p>
            <h1 data-testid={headingTestId}>{title}</h1>
            <p className="operations-board-subtitle">{subtitle}</p>
          </div>
          {actions && (
            <div className="operations-board-heading-actions">{actions}</div>
          )}
        </section>
        {children}
      </main>
    </div>
  );
}
